import moment from "moment";
import { Capacitor } from "@capacitor/core";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { sircapApi } from "shared/api/sircap";
import { PATH_LIST } from "shared/lib/react-router";
import { AssetType, EntityType, OperationType } from "static/types";
import numeral from "numeral";
import { IRate } from "entities/dashboard";
import i18n from "app/libs/i18n";
import { IAsset, marketStore } from "entities/market";
import { sessionStore } from "entities/session/model/sessionModel";
import { accountStore, KYC_Status } from "entities/account";

numeral.register("locale", "custom", {
  delimiters: {
    thousands: ".",
    decimal: ",",
  },
  abbreviations: {
    thousand: "K",
    million: "M",
    billion: "B",
    trillion: "T",
  },
  ordinal: function (number) {
    var b = number % 10;
    return ~~((number % 100) / 10) === 1
      ? "th"
      : b === 1
      ? "st"
      : b === 2
      ? "nd"
      : b === 3
      ? "rd"
      : "th";
  },
  currency: {
    symbol: "$",
  },
});
numeral.locale("custom");

export const b64toBlob = (
  srcData: string,
  contentType = "",
  sliceSize = 512,
) => {
  try {
    const base64Data = srcData.split(",")[1];
    const byteCharacters = atob(base64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: contentType });
    return blob;
  } catch (error) {}
};

export const blobToFile = (blob: Blob, filename: string): File => {
  return new File([blob], filename, { type: blob.type });
};

export const useSchema = (Schema: (t: any) => any) => {
  const { i18n, t } = useTranslation("yup");
  const schema = useMemo(() => Schema(t), [i18n.language]);

  return schema;
};

export function formatPrice(x: number, fixed: number = 2) {
  if (!x) return "";

  // 1) Готовим строку с фиксированной дробью
  const s = x.toFixed(fixed); // "200000.0000"
  const [intPart, fracPartRaw = ""] = s.split(".");

  // 2) Форматируем ТОЛЬКО целую часть
  const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  // 3) Убираем хвостовые нули из дробной части
  const frac = fracPartRaw.replace(/0+$/g, "");

  // 4) Склеиваем
  return frac ? `${intFormatted}.${frac}` : intFormatted;
}

export function formatMarketCap(x: number) {
  return x ? numeral(x).format("0.0a") : "";
}

const CDN_FOLDERS = {
  avatar: "avatars",
  chat: "chat",
  story: "gallery",
  strategy: "strategy",
  blog: "gallery",
  blog_internal: "blog",
  research: "gallery",
  member: "gallery",
  document: "documents",
  // reports/ на CDN закрыт — отчёты раздаёт бэкенд по подписанной ссылке.
};

type CDNFolder = keyof typeof CDN_FOLDERS;

// Записи старой админки хранят имя файла на CDN, Strapi отдаёт готовый
// абсолютный URL — такие возвращаем как есть, без префикса CDN.
export const getCdnUrl = (file: string, folder: CDNFolder) => {
  if (!file) return "";
  if (/^https?:\/\//i.test(file)) return file;
  return `${sircapApi.cdnUrl}/${CDN_FOLDERS[folder]}/${file}`;
};

export const getPreviewIccContractUrl = (
  user_id: number,
  user_type: string,
  amount: number,
  type: string,
) => {
  return `${sircapApi.baseUrl}/account/preview-agent-contract?amount=${amount}&type=${type}&user_id=${user_id}&user_type=${user_type}`;
};

/**
 * Открывает PDF поручения. Ссылка на файл живёт пару минут, поэтому в списках
 * её нет — берём у бэкенда в момент нажатия, и пересланная ссылка быстро
 * протухает.
 *
 * Вызывать прямо из обработчика клика: в браузере вкладку надо открыть
 * синхронно, пока действует жест пользователя, иначе её съест блокировщик
 * всплывающих окон. В нативном приложении вкладка не нужна — Capacitor сам
 * отдаёт переход на чужой домен (API) системному браузеру, а приложение
 * остаётся на месте.
 */
export const openAssignmentDocument = (id: number | string) => {
  const tab = Capacitor.isNativePlatform() ? null : window.open("", "_blank");

  sircapApi.account
    .assignmentContract(String(id))()
    .then((resp) => {
      const url: string | undefined = resp.data?.data?.document_url;
      if (!url) throw new Error("Assignment document link is missing");

      if (tab) {
        tab.opener = null;
        tab.location.href = url;
      } else {
        window.location.assign(url);
      }
    })
    .catch(() => {
      tab?.close();
      toast.error("Server Error");
    });
};

export const showNotification = (text: string) => {
  toast.info(text, {
    className: "notification-modal",
    bodyClassName: "notification-modal-text",
    position: "bottom-center",
    icon: false,
    hideProgressBar: true,
  });
};

export const navigateToAsset = (
  type: number,
  asset_id: any,
  navigate: any,
  operation: string = "",
) => {
  switch (type) {
    case AssetType.Stock:
    case AssetType.Fond:
    case AssetType.ETF:
      navigate(PATH_LIST.stock(asset_id));
      break;

    case AssetType.Currency:
      navigate(PATH_LIST.currency(asset_id));
      break;

    case AssetType.Bond:
    case AssetType.EuroBond:
      navigate(PATH_LIST.bond(asset_id));
      break;

    case AssetType.StructuredProduct:
      navigate(PATH_LIST.strategyInvest(asset_id, operation));
      break;

    case AssetType.Option:
      navigate(PATH_LIST.optionChain(asset_id));
      break;

    default:
      break;
  }
};

export const navigateToAssetByWill = (
  type: number,
  asset_id: any,
  operation: string = "",
) => {
  switch (type) {
    case AssetType.Stock:
    case AssetType.Fond:
    case AssetType.ETF:
      return PATH_LIST.stock(asset_id);

    case AssetType.Currency:
      return PATH_LIST.currency(asset_id);

    case AssetType.Bond:
    case AssetType.EuroBond:
      return PATH_LIST.bond(asset_id);

    case AssetType.StructuredProduct:
      return PATH_LIST.strategyInvest(asset_id, operation);

    case AssetType.Option:
      return PATH_LIST.optionChain(asset_id);

    default:
      break;
  }
};

export const getCommission = (
  commision: number,
  amount: number,
  operation: string,
  minimal_commission: number,
  with_sirius: boolean,
  additional_amount: number = 0,
  min_commission_currency?: string | null,
  asset_currency_code?: string | null,
  min_commission_cb_rate?: number | null,
) => {
  if (with_sirius) return [0, 0, 0];

  const percentRaw = (amount / 100) * commision;
  const isCrossCurrencyMin = !!min_commission_currency && !!asset_currency_code
    && min_commission_currency !== asset_currency_code;
  let totalCommission: number;
  if (isCrossCurrencyMin && minimal_commission && min_commission_cb_rate) {
    // Min is in another currency — convert to asset currency for comparison
    const minInAsset = minimal_commission / min_commission_cb_rate;
    totalCommission = +Math.max(percentRaw, minInAsset).toFixed(2);
  } else if (!isCrossCurrencyMin && minimal_commission) {
    totalCommission = +Math.max(percentRaw, minimal_commission).toFixed(2);
  } else {
    totalCommission = +percentRaw.toFixed(2);
  }

  // let totalAmount: number;
  // if (operation === OperationType.Sell) {
  //   totalAmount = amount - totalCommission;
  // } else {
  //   totalAmount = amount + totalCommission;
  // }
  // return [commision, additional, totalAmount];

  const totalAmount =
    operation === OperationType.Sell ? totalCommission * -1 : totalCommission;

  return [commision, additional_amount, totalAmount];
};

const getAllRates = () => {
  const assetsCurrencies = marketStore.getState().assetsCurrencies;
  const allRates: IRate[] = [];
  for (const asset of assetsCurrencies) {
    allRates.push({
      pair: `${asset.symbol}/${asset.currency.code}`,
      from: asset.symbol,
      to: asset.currency.code,
      bid: asset.bid,
      ask: asset.ask,
    });
  }

  return allRates;
};

export const getRatePrice = (
  rate: { trade_asset: any; price_asset: any },
  type: "ask" | "bid" | "middle",
) => {
  const all_rates = getAllRates();
  const rateExists = all_rates.find(
    (r) => r.from === rate.trade_asset && r.to === rate.price_asset,
  );

  let price = null;
  if (rateExists) {
    if (type === "ask") {
      price = +rateExists.ask.toFixed(4);
    } else if (type === "bid") {
      price = +rateExists.bid.toFixed(4);
    } else {
      price = +((rateExists.ask + rateExists.bid) / 2).toFixed(4);
    }
  } else {
    const rateSwapExists = all_rates.find(
      (r) => r.to === rate.trade_asset && r.from === rate.price_asset,
    );
    if (rateSwapExists) {
      if (type === "ask") {
        price = +(1 / rateSwapExists.ask).toFixed(4);
      } else if (type === "bid") {
        price = +(1 / rateSwapExists.bid).toFixed(4);
      } else {
        price = +(1 / ((rateSwapExists.ask + rateSwapExists.bid) / 2)).toFixed(
          4,
        );
      }
    }
  }

  return price;
};

export const getDirectOrCrosse = (
  assetsCurrencies: IAsset[],
  currency: string,
  code: string,
): "direct" | "crosse" => {
  return assetsCurrencies.find(
    (item) => item.symbol === currency && item.currency?.code === code,
  )
    ? "direct"
    : "crosse";
};

export const convertTo =
  (currency: string, type: "direct" | "crosse") =>
  (item: { symbol: string; amount: number }) => {
    const { symbol, amount } = item;

    if (symbol === currency) {
      return amount;
    } else {
      const price = getRatePrice(
        {
          trade_asset: type === "direct" ? currency : symbol,
          price_asset: type === "direct" ? symbol : currency,
        },
        "middle",
      );
      if (price) {
        return type === "direct" ? amount / price : amount * price;
      } else {
        return 0;
      }
    }
  };

export const getCurrSymbol = (code: string) => {
  const symbols: Record<string, string> = {
    AMD: "֏",
    USD: "$",
    EUR: "€",
    RUB: "₽",
    AED: "د.إ",
    CNY: "¥",
    GBP: "£",
  };

  return symbols[code];
};

export const isBondMatured = (maturity_date: string | null) => {
  if (maturity_date) {
    const format = "YYYY-MM-DD";
    const maturityDate = moment(maturity_date, format);
    if (maturityDate.isBefore(moment().add(5, "day"))) {
      return true;
    }
  }
  return false;
};

export type MultiLangValueType = Record<string, string>;

export const getMLValue = (field: MultiLangValueType, locale = "en") => {
  return field ? field[locale] || field[Object.keys(field)[0]] || "" : "";
};

export const getFromLotsText = (lots: number, t: any) => {
  return `${lots} ${lots === 1 ? t("lot") : t("lots")}`;
};

export type MultiLangField = Record<string, string>;

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? match[2] : null;
}

export function getOrCreateDeviceUUID(): string {
  let uuid = localStorage.getItem("device_uuid") || getCookie("device_uuid");
  if (!uuid) {
    try {
      uuid = crypto.randomUUID();
    } catch (error) {
      uuid = Date.now().toString() + Math.random().toString(16).slice(2);
    }

    localStorage.setItem("device_uuid", uuid);

    // Можно продублировать в cookie
    document.cookie = `device_uuid=${uuid}; path=/; max-age=31536000; Secure; SameSite=Strict`;
  } else {
    localStorage.setItem("device_uuid", uuid);
  }

  return uuid;
}

export function getSubAccountId(): string {
  return sessionStore.getState().subAccountSubNumber || "";
}

// User-id based access groups. // todo: replace with backend-driven permissions / feature flags once available.
export const HEADS_USER_IDS: readonly number[] = [32, 35];

export const QA_USER_IDS: readonly number[] = [
  ...HEADS_USER_IDS,
  // todo: QA ids here - ...HEADS_USER_IDS,10,12,14,16,18,20
];

interface UserAccessOptions {
  extraIds?: number[];
  bypassInDev?: boolean;
}

const IS_PROD = process.env.NODE_ENV === "production";

const matchUserId = (
  userId: number | null | undefined,
  baseIds: readonly number[],
  { extraIds, bypassInDev }: UserAccessOptions = {},
): boolean => {
  if (bypassInDev && !IS_PROD) return true;
  if (userId == null) return false;
  if (baseIds.includes(userId)) return true;
  return extraIds?.includes(userId) ?? false;
};

export const isHeadUser = (
  userId: number | null | undefined,
  options?: UserAccessOptions,
): boolean =>
  matchUserId(userId, HEADS_USER_IDS, { bypassInDev: true, ...options });

export const isQAUser = (
  userId: number | null | undefined,
  options?: UserAccessOptions,
): boolean =>
  matchUserId(userId, QA_USER_IDS, { bypassInDev: true, ...options });

export const isUserInList = (
  userId: number | null | undefined,
  allowedIds: number[],
  options?: Pick<UserAccessOptions, "bypassInDev">,
): boolean => matchUserId(userId, allowedIds, options);


export const getCurrencySymbol = (code: string, list: any) => {
  return list.find((c: any) => c.code === code)?.symbol || "";
};

export const timer = (s: number) => {
  return new Promise((res) => setTimeout(res, s * 1000));
  // timer(0.2).then(() => {
  //   func();
  // });
};

export const formatDate = (dateString: string) => {
  if (!dateString) return "";
  return moment(dateString).locale(i18n.language).format("DD MMMM YYYY");
};

export const checkCanCancelDeal = (
  deal: any,
): { canCancel: boolean; hoursUntilNext: number; minutesUntilNext: number } => {
  const asset = deal.asset as unknown as IAsset;
  const ex = asset.exchange;
  if (!ex) return { canCancel: true, hoursUntilNext: 0, minutesUntilNext: 0 };

  if (!ex.cancel_from)
    return { canCancel: true, hoursUntilNext: 0, minutesUntilNext: 0 };

  const now = moment.utc();
  const localNow = now.clone().tz(ex.timezone);

  const cancelEndTime =
    ex.after_close && ex.after_close !== "00:00" ? ex.after_close : ex.close;

  const dealDate = moment(deal.createdAt).tz(ex.timezone);
  if (localNow.get("date") === dealDate.get("date")) {
    const end = moment.tz(
      localNow.format("YYYY-MM-DD") + " " + cancelEndTime,
      "YYYY-MM-DD HH:mm",
      ex.timezone,
    );

    const canCancel = localNow.isBefore(end, "minute");
    const hoursUntilNext = canCancel ? end.diff(localNow, "hours", true) : 0;
    const minutesUntilNext = canCancel
      ? end.diff(localNow, "minutes", true)
      : 0;

    return { canCancel, hoursUntilNext, minutesUntilNext };
  }

  const dow = localNow.day();
  if (dow === 0 || dow === 6) {
    let daysToAdd = dow === 0 ? 1 : 2;

    const nextTradingDate = localNow.clone().add(daysToAdd, "days");
    if (ex.isHoliday) {
      daysToAdd++;
      while (nextTradingDate.day() === 0 || nextTradingDate.day() === 6) {
        daysToAdd++;
        nextTradingDate.add(1, "day");
      }
    }

    const nextTradingStart = moment.tz(
      localNow.clone().add(daysToAdd, "days").format("YYYY-MM-DD") +
        " " +
        ex.cancel_from,
      "YYYY-MM-DD HH:mm",
      ex.timezone,
    );

    const hoursUntilNext = nextTradingStart.diff(localNow, "hours", true);
    const minutesUntilNext = nextTradingStart.diff(localNow, "minutes", true);
    return { canCancel: false, hoursUntilNext, minutesUntilNext };
  }

  const start = moment.tz(
    localNow.format("YYYY-MM-DD") + " " + ex.cancel_from,
    "YYYY-MM-DD HH:mm",
    ex.timezone,
  );
  const end = moment.tz(
    localNow.format("YYYY-MM-DD") + " " + cancelEndTime,
    "YYYY-MM-DD HH:mm",
    ex.timezone,
  );

  const isBetween = localNow.isBetween(start, end, "minute", "[)");

  if (isBetween) {
    const hoursUntilNext = end.diff(localNow, "hours", true);
    const minutesUntilNext = end.diff(localNow, "minutes", true);
    return { canCancel: true, hoursUntilNext, minutesUntilNext };
  } else {
    let nextTradingStart;

    if (localNow.isBefore(start)) {
      nextTradingStart = start;
    } else {
      const nextDay = localNow.clone().add(1, "day");
      const nextDow = nextDay.day();

      if (nextDow === 0 || nextDow === 6) {
        const daysToAdd = nextDow === 0 ? 1 : 2;
        nextTradingStart = moment.tz(
          localNow.clone().add(daysToAdd, "days").format("YYYY-MM-DD") +
            " " +
            ex.cancel_from,
          "YYYY-MM-DD HH:mm",
          ex.timezone,
        );
      } else {
        nextTradingStart = moment.tz(
          nextDay.format("YYYY-MM-DD") + " " + ex.cancel_from,
          "YYYY-MM-DD HH:mm",
          ex.timezone,
        );
      }
    }

    const hoursUntilNext = nextTradingStart.diff(localNow, "hours", true);
    const minutesUntilNext = nextTradingStart.diff(localNow, "minutes", true);
    return { canCancel: false, hoursUntilNext, minutesUntilNext };
  }
};

export const formatTimeUntilNextTradingSession = (
  hoursUntilNext: number,
  minutesUntilNext: number,
  t: (key: string) => string,
): string => {
  if (hoursUntilNext <= 0 && minutesUntilNext <= 0) {
    return "";
  }

  const totalMinutes = Math.ceil(hoursUntilNext * 60 + minutesUntilNext);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  // if (hours > 0 && minutes > 0) {
  //   return ` ${t("nextTradingSessionStartsIn")} ${hours} ${
  //     hours > 1 ? t("hours") : t("hour")
  //   } ${minutes} ${minutes > 1 ? t("minutes") : t("minute")}`;
  // } else if (hours > 0) {
  //   return ` ${t("nextTradingSessionStartsIn")} ${hours} ${
  //     hours > 1 ? t("hours") : t("hour")
  //   }`;
  // } else {
  //   return ` ${t("nextTradingSessionStartsIn")} ${minutes} ${
  //     minutes > 1 ? t("minutes") : t("minute")
  //   }`;
  // }

  return `${t("pleaseWait")}`;
};

export const canCancelDeal = (
  deal: any,
  t: (key: string) => string,
): { canCancel: boolean; errorMessage?: string } => {
  const { canCancel, hoursUntilNext, minutesUntilNext } =
    checkCanCancelDeal(deal);

  if (canCancel) {
    return { canCancel: true };
  } else {
    const timeText = formatTimeUntilNextTradingSession(
      hoursUntilNext,
      minutesUntilNext,
      t,
    );
    return {
      canCancel: false,
      errorMessage: `${t("youCanNotCancelDeal")}${timeText}`,
    };
  }
};

export const getTimeUntilNextTradingSession = (
  deal: any,
  t: (key: string) => string,
): string => {
  const { canCancel, hoursUntilNext, minutesUntilNext } =
    checkCanCancelDeal(deal);

  if (canCancel) {
    return "";
  }

  return formatTimeUntilNextTradingSession(hoursUntilNext, minutesUntilNext, t);
};

export const hasSymbolInBrokerageAccount = (
  brokerageAccount: any,
  symbol: string,
) => {
  return (
    brokerageAccount?.some((item: any) => item.asset?.symbol === symbol) ||
    false
  );
  // example:
  // hasSymbolInBrokerageAccount(
  //   getUserBrokerageAccount(),
  //   "SPY"
  // )
};

// export function loadIfEmpty<K extends keyof any>(
//   key: K,
//   isLoading: boolean,
//   get: () => any,
//   set: (partial: Partial<any>) => void
// ) {
//   const arr = get()[key];
//   if (Array.isArray(arr) && arr.length === 0) {
//     set({ isLoading });
//   }
// }

// export const getAssetPrice = (
//   assetInfo: IAssetInfo | null,
//   type: "ask" | "bid" | "mid"
// ) => {
//   try {
//     if ([AssetType.ETF, AssetType.Stock, AssetType.Fond].includes(+type)) {
//       const info = assetInfo?.asset;
//       if (info) return info.ask;
//     } else if ([AssetType.Bond, AssetType.EuroBond].includes(+type)) {
//       const info = assetInfo?.asset;
//       if (info) return info.ask;
//     } else {
//       const askPrice = assetInfo?.asset?.ask;
//       if (askPrice) {
//         return +askPrice.toFixed(2);
//       }
//     }
//     return 0;
//   } catch (error) {
//     return 0;
//   }
// };

//! low quality placeholder
// export const createLowQualityPlaceholder = (
//   imageUrl: string,
//   quality: number = 0.1
// ): Promise<string> => {
//   return new Promise((resolve, reject) => {
//     const img = new Image();
//     img.crossOrigin = "Anonymous";
//     img.onload = () => {
//       const canvas = document.createElement("canvas");
//       const ctx = canvas.getContext("2d");
//       if (!ctx) return reject("Canvas context not found");

//       canvas.width = img.width / 10;
//       canvas.height = img.height / 10;

//       ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

//       const dataUrl = canvas.toDataURL("image/jpeg", quality);
//       resolve(dataUrl);
//     };
//     img.onerror = (err) => reject(err);
//     img.src = imageUrl;
//   });
// };

export const userVerified = () => {
  const state = accountStore.getState();
  const entity = sessionStore.getState().entity;
  const status =
    entity === EntityType.Individual ? state.kyc?.status : state.kyb?.status;
  return status === KYC_Status.Success;
};
